import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../../constants";
import Preloader from "../../user/Preloader";
import { FormGenerator } from "../../helper/FormGenerator";
import { isNumber } from "chart.js/helpers";
import { useTokens } from "../../context/TokenContext";

const AdminUserAddresses = ({ id }) => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const [loading, setLoading] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [isPincodeValid, setIsPincodeValid] = useState(false);
  const [formData, setFormData] = useState({
    user_id: id,
    name: "",
    mobileNumber: "",
    pincode: "",
    locality: "",
    address: "",
    city: "",
    state: "",
    country: "India",
    landmark: "",
    alternatePhone: "",
    addressType: "home",
  });
  const [editingAddressId, setEditingAddressId] = useState(null);

  const token = adminAuthToken;

  useEffect(() => {
    // Fetch addresses from API on component mount
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/addresses`, {
        params: { searchBy: 'user', searchValue: id },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setAddresses(response.data);
    } catch (error) {

    }
    setLoading(false);
  };

  const handleToggleForm = () => {
    setIsFormVisible(!isFormVisible);
    setFormData({
      user_id: id,
      name: "",
      mobileNumber: "",
      pincode: "",
      locality: "",
      address: "",
      country: "India",
      city: "",
      state: "",
      landmark: "",
      alternatePhone: "",
      addressType: "home",
    });
    setEditingAddressId(null); // Reset editing
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleRadioChange = (e) => {
    setFormData({
      ...formData,
      addressType: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const toastId = toast.loading(`Saving address...`, { autoClose: false });

    try {
      const method = editingAddressId ? "put" : "post";
      const url = editingAddressId
        ? `${API_URL}/addresses/${editingAddressId}`
        : `${API_URL}/addresses`;
      const response = await axios[method](url, formData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (editingAddressId) {
        setAddresses(
          addresses.map((address) =>
            address.id === editingAddressId ? response.data : address
          )
        );
      } else {
        setAddresses([...addresses, response.data]);
      }

      toast.update(toastId, { render: "Address saved successfully", type: "success", autoClose: 2000, isLoading: false });
      setFormData({});
      handleToggleForm();
    } catch (error) {
      toast.update(toastId, { render: "Failed to save address", type: "error", autoClose: 2000, isLoading: false });
    }
  };

  const handleEdit = (address) => {
    setEditingAddressId(address.id);
    setFormData({
      name: address.name,
      mobileNumber: address.mobileNumber,
      pincode: address.pincode,
      locality: address.locality,
      address: address.address,
      city: address.city,
      country: address.country,
      state: address.state,
      landmark: address.landmark || "",
      alternatePhone: address.alternatePhone || "",
      addressType: address.addressType,
    });
    setIsFormVisible(true);
  };

  const handleCancel = () => {
    handleToggleForm();
  };

  const handleDelete = async (id) => {
    const toastId = toast.loading(`Deleting...`, { autoClose: false });
    try {
      await axios.delete(`${API_URL}/addresses/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      setAddresses(addresses.filter((address) => address.id !== id));
      toast.update(toastId, { render: "Address deleted successfully", type: "success", autoClose: 2000, isLoading: false });
    } catch (error) {
      toast.update(toastId, { render: "Failed to delete address", type: "error", autoClose: 2000, isLoading: false });
    }
  };

  return (
    <div>
      <button className="btn btn-primary w-100" onClick={handleToggleForm}>
        {isFormVisible ? <><i className="bi bi-x-lg"></i> Hide Form</> : <><i className="bi bi-plus-lg"></i> Add Address</>}
      </button>

      {isFormVisible && (
        <div className="address-form">
          <h4 className="fw-bold my-3">{editingAddressId ? "Edit Address" : "Add Address"}</h4>
          <form className="border p-3" onSubmit={handleSubmit} >
            <div className="row">
              <FormGenerator
                fields={[
                  { id: 'name', label: 'Name', type: 'text', class: 'col-md-12', required: true },
                  { id: 'pincode', label: 'Pincode', type: 'text', class: 'col-md-6', required: true, maxLength: 6, minLength: 6, isNumeric: true },
                  { id: 'locality', label: 'Locality', type: 'text', class: 'col-md-6', required: true },
                  { id: 'address', label: 'Address (Area and Street)', type: 'textarea', class: 'col-md-12', required: true },
                  { id: 'city', label: 'City/District/Town', type: 'text', class: 'col-md-4', required: true },
                  { id: 'state', label: 'State', type: 'text', class: 'col-md-4', required: true },
                  { id: 'country', label: 'Country', type: 'select', options: {'India': 'India'}, default: 'India', class: 'col-md-4', required: true },
                  { id: 'landmark', label: 'Landmark', type: 'text', class: 'col-md-6', required: false },
                  { id: 'alternatePhone', label: 'Alternate Phone', type: 'text', class: 'col-md-6', required: false, maxLength: 10, isNumeric: true },
                  { id: 'addressType', label: 'Address Type', type: 'radio', class: 'col-md-6', options: ['home', 'work'], required: true },
                ]}
                fieldData={formData}
                onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                onSubmit={false}
                hTure={false}
              />
            </div>

            <div className="mt-4">
              <button className="btn btn-primary me-2" type="submit">
                Save
              </button>
              <button className="btn btn-secondary" type="button" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <h4 className="fw-bold my-3">Addresses</h4>
      {loading ? <Preloader /> :
        <ul className="list-group">
          {addresses.map((address) => (
            <li className="list-group-item list-group-item-action" key={address.id}>
              <div className="d-flex w-100 justify-content-between">
                <h5 className="mb-1 badge bg-secondary-subtle text-uppercase">{address.addressType}</h5>
                <div className="dropdown">
                  <button className="btn btn-sm rounded-0 btn-outline-danger p-0 px-1" data-bs-toggle="dropdown">
                    <i className="bi bi-three-dots"></i>
                  </button>
                  <div className="dropdown-menu dropdown-menu-left">
                    <button className="dropdown-item" onClick={() => handleEdit(address)}>Edit</button>
                    <button className="dropdown-item" onClick={() => handleDelete(address.id)}>Delete</button>
                  </div>
                </div>
              </div>
              <h6 className="fw-bold my-1">{address.name} <strong>{address.mobileNumber}</strong></h6>
              <span>{address.address}, {address.locality}, {address.landmark}, <br />{address.city},  {address.state}, <strong>{address.country} - {address.pincode}</strong></span>
            </li>
          ))}
        </ul>
      }
    </div>
  );
};

export default AdminUserAddresses;
